/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState, useRef, useContext } from 'react';
import { get } from 'lodash';
import { Button as GButton } from 'grommet';

import { completeTask, internalUpdateTask } from 'blb-api/tasks';
import { IFrameModal } from 'blb-components/GrommetComponents';
import { MeContext } from 'blb-contexts/Me';
import { BlendBridge } from 'blb-types/blendBridge';
import { errorMessage } from 'blb-utils/errors';

export namespace IframeTaskModal {
  export interface Props {
    children?: JSX.Element;
    handleClose: () => void;
    id?: string;
    onSuccess: () => Promise<any>;
    subtitleComponents?: JSX.Element;
    taskId: string;
    title: string;
    url: string;
  }
}

export const IframeTaskModal: React.FC<IframeTaskModal.Props> = ({
  id,
  children,
  onSuccess,
  taskId,
  title,
  subtitleComponents,
  handleClose,
  url,
}) => {
  const me = useContext(MeContext);
  const [serviceError, setServiceError] = useState<string | undefined>(undefined);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const isMountedRef = useRef(true);
  id = id ?? `it-task-${taskId}`;

  const handleIframeModalClose = async () => {
    handleClose();
    setServiceError(undefined);
    // set task back to OPEN:
    // Tasks, in the past and potentially in the future, were LOCKED upon action.
    // This resets the task back to OPEN for eSign docs to be signed Day Of.
    await internalUpdateTask(taskId, { status: BlendBridge.TaskStatuses.OPEN });
  };

  const handleComplete = async () => {
    try {
      setButtonDisabled(true);
      await completeTask(taskId);

      handleClose();
      isMountedRef.current = false;
      await onSuccess();
    } catch (error) {
      if (isMountedRef.current) {
        const serviceErrorMessage =
          get(error, 'response.data.error.serviceErrorMessage') ||
          get(error, 'response.data.error.message', errorMessage('ERROR_COMPLETING_TAGGING', me?.principalType));
        setServiceError(serviceErrorMessage);
        return;
      }
      throw error;
    } finally {
      if (isMountedRef.current) {
        setButtonDisabled(false);
      }
    }
  };

  const iframeCompleteButton = (
    <GButton
      data-track-element-name="FullScreenModalCloseBtn"
      disabled={buttonDisabled}
      id={`${id}-iframe-completeBtn`}
      label="Complete"
      margin="xxsmall"
      onClick={handleComplete}
      primary
      size="medium"
    />
  );

  return (
    <IFrameModal
      actionsComponent={iframeCompleteButton}
      error={serviceError}
      handleClose={handleIframeModalClose}
      id={id}
      subtitleComponent={subtitleComponents}
      title={title}
      url={url}
    >
      {children}
    </IFrameModal>
  );
};
